import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectClinicProfile,
  selectIsAdmin,
  selectHasPermission,
  selectAllowMultipleTherapists,
  selectClinicSharesTreatments,
} from "../../store/slices/authSlice";
import {
  showGlobalLoader,
  hideGlobalLoader,
  selectOnboardingActive,
} from "../../store/slices/uiSlice";
import {
  useGetActiveLiveSessionQuery,
  useCreateLiveSessionMutation,
  useUpdateLiveSessionMutation,
  useEndLiveSessionMutation,
} from "../../store/services/liveSessionsApi";
import { useGetPatientQuery } from "../../store/services/patientsApi";
import { useGetTreatmentsForPatientQuery } from "../../store/services/treatmentsApi";
import { useGetTemplatesQuery } from "../../store/services/formsApi";
import { useGetAppointmentsQuery, calendarApi } from "../../store/services/calendarApi";
import {
  useGetClinicGamesQuery,
  getGoalsByIds,
} from "../../store/services/gamesApi";
import { useToast } from "../../contexts/ToastContext";
import { useUnsavedChangesWarning } from "../../hooks/useUnsavedChangesWarning";
import {
  aiGameSelection,
  aiPatientConsultation,
  aiTreatmentConsultation,
} from "../../firebase";
import {
  Clock,
  Timer,
  Play,
  Stop,
  Plus,
  Trash,
  FloppyDisk,
  Check,
  FileText,
  Sparkle,
  GameController,
  Brain,
  ChatCircle,
  ArrowsOut,
  ArrowsIn,
  X,
  User,
  CalendarBlank,
  Stethoscope,
  NotePencil,
  Lightning,
  WarningCircle,
  PencilSimple,
} from "@phosphor-icons/react";
import RichTextEditor from "../../components/ui/RichTextEditor";
import HandwritingCanvas, { HandwritingPreview } from "../../components/ui/HandwritingCanvas";
import { HelpButton } from "../../components/shared";
import ContentLoader from "../../components/shared/ContentLoader";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import ModalCloseButton from "../../components/ui/ModalCloseButton";
import "./LiveSessionPage.css";

// Default template (same as TreatmentDocPage)
const DEFAULT_TEMPLATE = {
  id: "default",
  name: "סיכום בלייב",
  fields: [
    { id: "title", label: "כותרת", type: "short_text", required: true },
    {
      id: "content",
      label: "תוכן הטיפול",
      type: "long_text",
      required: true,
    },
  ],
};

// Format time for display
const formatTime = (date) => {
  return date.toLocaleTimeString("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Format remaining time to meeting end (mm:ss)
const formatRemainingTime = (endTime) => {
  if (!endTime) return "00:00";
  
  const now = new Date();
  const end = new Date(endTime);
  const diffMs = end - now;
  
  if (diffMs <= 0) return "00:00";
  
  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

function LiveSessionPage() {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSuccess, showError, showWarning } = useToast();

  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isOnboardingActive = useSelector(selectOnboardingActive);
  const clinicProfile = useSelector(selectClinicProfile);
  const isSpeechTherapist = clinicProfile?.type === "speech_therapist";

  // Get patient ID from search params
  const patientId = searchParams.get("patientId");

  // State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [remainingTime, setRemainingTime] = useState("00:00");
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [formData, setFormData] = useState({
    templateId: "default",
    fields: DEFAULT_TEMPLATE.fields.map((f) => ({ ...f, value: "" })),
    liveNotes: [],
    selectedGameIds: [],
    handwritingStrokes: [],
    handwritingCanvasSize: null,
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [showHandwritingCanvas, setShowHandwritingCanvas] = useState(false);

  // AI Tools State
  const [aiToolModal, setAiToolModal] = useState(null); // 'game' | 'patient' | 'treatment' | null
  const [aiToolInput, setAiToolInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponses, setAiResponses] = useState([]); // Array of { id, type, question, response, timestamp }

  // Refs
  const autoSaveTimerRef = useRef(null);
  const lastSavedDataRef = useRef(null);

  // Queries
  const { data: patient, isLoading: isLoadingPatient } = useGetPatientQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );

  const { data: templates = [] } = useGetTemplatesQuery(
    { clinicId },
    { skip: !clinicId }
  );

  const { data: clinicGames = [] } = useGetClinicGamesQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Same gating as PatientProfilePage: an unfiltered list is only allowed by
  // rules for admin / treatments.viewAllClinic / shared-treatments clinics /
  // the primary therapist / a co-therapist with treatments.viewSharedPatient.
  // Anyone else is scoped to their own reports (avoids permission-denied).
  const isAdmin = useSelector(selectIsAdmin);
  const canViewAllTreatments = useSelector(selectHasPermission("treatments.viewAllClinic"));
  const canViewSharedTreatments = useSelector(selectHasPermission("treatments.viewSharedPatient"));
  const allowMultipleTherapists = useSelector(selectAllowMultipleTherapists);
  const sharesTreatments = useSelector(selectClinicSharesTreatments);
  const isPrimaryTherapist =
    !!patient?.therapistId && patient.therapistId === therapistId;
  const isCoTherapistOfPatient =
    Array.isArray(patient?.therapistIds) &&
    patient.therapistIds.includes(therapistId);
  const canListAllPatientTreatments =
    isAdmin ||
    canViewAllTreatments ||
    sharesTreatments ||
    isPrimaryTherapist ||
    (allowMultipleTherapists &&
      isCoTherapistOfPatient &&
      canViewSharedTreatments !== false);

  // Get patient's treatment history for last session summary.
  // Own-scope uses therapistId WITHOUT status (no 4-field index exists);
  // "locked" is then filtered in memory below.
  const { data: patientTreatments = [] } = useGetTreatmentsForPatientQuery(
    canListAllPatientTreatments
      ? { clinicId, patientId, status: "locked" }
      : { clinicId, patientId, therapistId },
    { skip: !clinicId || !patientId || !patient }
  );

  // Get the last treatment summary (most recent locked treatment)
  const lastTreatmentSummary = useMemo(() => {
    // Treatments are already sorted by date descending; skip soft-deleted reports
    const lastTreatment = (patientTreatments || []).find(
      (t) => t.isDeleted !== true && t.status === "locked"
    );
    if (!lastTreatment) {
      return "אין עדיין פגישות מתועדות למטופל זה.";
    }
    return lastTreatment.summary || lastTreatment.shortSummary || "לא נמצא סיכום לפגישה האחרונה.";
  }, [patientTreatments]);

  // Get today's appointments to find the ongoing one
  const today = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    return { startOfDay, endOfDay };
  }, []);

  const { data: appointments = [] } = useGetAppointmentsQuery(
    {
      clinicId,
      startDate: today.startOfDay.toISOString(),
      endDate: today.endOfDay.toISOString(),
      therapistId,
    },
    { skip: !clinicId || !therapistId }
  );

  // Find the ongoing appointment
  const ongoingAppointment = useMemo(() => {
    if (appointmentId) {
      return appointments.find((apt) => apt.id === appointmentId);
    }
    return null;
  }, [appointments, appointmentId]);

  // Check for existing active session
  const { data: existingSession, isLoading: isLoadingSession } =
    useGetActiveLiveSessionQuery(
      { clinicId, therapistId, appointmentId },
      { 
        skip: !clinicId || !therapistId || !appointmentId,
        refetchOnMountOrArgChange: true 
      }
    );

  // Mutations
  const [createSession, { isLoading: isCreating }] =
    useCreateLiveSessionMutation();
  const [updateSession, { isLoading: isUpdating }] =
    useUpdateLiveSessionMutation();
  const [endSession, { isLoading: isEnding }] = useEndLiveSessionMutation();

  // Unsaved changes warning
  useUnsavedChangesWarning(hasUnsavedChanges);

  useEffect(() => {
    trackFeature(FEATURE_KEYS.LIVE_SESSION);
  }, []);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      // Update remaining time based on appointment end time
      if (ongoingAppointment?.endTime) {
        setRemainingTime(formatRemainingTime(ongoingAppointment.endTime));
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [ongoingAppointment]);

  // Initialize from existing session or create new one
  useEffect(() => {
    if (existingSession) {
      // Load existing session data (including when returning to page)
      setActiveSessionId(existingSession.id);
      setSessionStartedAt(existingSession.startedAt);
      setFormData({
        templateId: existingSession.templateId || "default",
        fields: existingSession.fields || DEFAULT_TEMPLATE.fields.map((f) => ({ ...f, value: "" })),
        liveNotes: existingSession.liveNotes || [],
        selectedGameIds: existingSession.selectedGameIds || [],
        handwritingStrokes: existingSession.handwritingStrokes || [],
        handwritingCanvasSize: existingSession.handwritingCanvasSize || null,
      });
      lastSavedDataRef.current = JSON.stringify({
        templateId: existingSession.templateId,
        fields: existingSession.fields,
        liveNotes: existingSession.liveNotes,
        selectedGameIds: existingSession.selectedGameIds,
        handwritingStrokes: existingSession.handwritingStrokes,
        handwritingCanvasSize: existingSession.handwritingCanvasSize,
      });
      setHasUnsavedChanges(false);
    }
  }, [existingSession]);

  // Temp customers (לקוח מזדמן) have no live session — leave the page
  useEffect(() => {
    if (ongoingAppointment?.isTempCustomer === true) {
      navigate("/dashboard", { replace: true });
    }
  }, [ongoingAppointment, navigate]);

  // Create session if none exists
  useEffect(() => {
    const createNewSession = async () => {
      if (
        !existingSession &&
        !isLoadingSession &&
        !activeSessionId &&
        clinicId &&
        therapistId &&
        patientId &&
        appointmentId &&
        patient &&
        ongoingAppointment &&
        ongoingAppointment.isTempCustomer !== true
      ) {
        try {
          const result = await createSession({
            clinicId,
            sessionData: {
              patientId,
              patientName: `${patient.firstName} ${patient.lastName}`,
              appointmentId,
              appointmentStartTime: ongoingAppointment.startTime,
              appointmentEndTime: ongoingAppointment.endTime,
              treatmentTypeId: ongoingAppointment.treatmentTypeId || null,
              templateId: "default",
              fields: DEFAULT_TEMPLATE.fields.map((f) => ({ ...f, value: "" })),
              liveNotes: [],
              selectedGameIds: [],
              handwritingStrokes: [],
              handwritingCanvasSize: null,
            },
            therapistId,
            therapistName,
          }).unwrap();

          setActiveSessionId(result.id);
          setSessionStartedAt(result.startedAt || new Date().toISOString());
          showSuccess("סשן לייב התחיל");
        } catch (error) {
          console.error("Error creating live session:", error);
          showError("שגיאה בהתחלת סשן לייב");
        }
      }
    };

    createNewSession();
  }, [
    existingSession,
    isLoadingSession,
    activeSessionId,
    clinicId,
    therapistId,
    therapistName,
    patientId,
    appointmentId,
    patient,
    ongoingAppointment,
    createSession,
    showSuccess,
    showError,
  ]);

  // Auto-save every 4 minutes
  const performAutoSave = useCallback(async () => {
    if (!activeSessionId || !clinicId) return;

    const currentDataStr = JSON.stringify(formData);
    if (lastSavedDataRef.current === currentDataStr) return;

    setAutoSaveStatus("saving");

    try {
      await updateSession({
        clinicId,
        sessionId: activeSessionId,
        sessionData: formData,
        therapistId,
      }).unwrap();

      lastSavedDataRef.current = currentDataStr;
      setAutoSaveStatus("saved");
      setHasUnsavedChanges(false);

      setTimeout(() => setAutoSaveStatus(null), 3000);
    } catch (error) {
      console.error("Auto-save failed:", error);
      setAutoSaveStatus(null);
    }
  }, [activeSessionId, clinicId, formData, therapistId, updateSession]);

  // Setup auto-save timer (every 4 minutes)
  useEffect(() => {
    if (!activeSessionId) return;

    autoSaveTimerRef.current = setInterval(() => {
      performAutoSave();
    }, 4 * 60 * 1000); // 4 minutes

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [activeSessionId, performAutoSave]);

  // Track changes
  useEffect(() => {
    const currentDataStr = JSON.stringify(formData);
    if (lastSavedDataRef.current && lastSavedDataRef.current !== currentDataStr) {
      setHasUnsavedChanges(true);
    }
  }, [formData]);

  // Template selection handler
  const handleTemplateSelect = (template) => {
    setFormData((prev) => ({
      ...prev,
      templateId: template.id,
      fields: template.fields.map((f) => ({
        ...f,
        value: prev.fields.find((pf) => pf.id === f.id)?.value || "",
      })),
    }));
  };

  // Field change handler
  const handleFieldChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.id === fieldId ? { ...f, value } : f)),
    }));
  };

  // Add live note
  const handleAddLiveNote = () => {
    const newNote = {
      id: `note_${Date.now()}`,
      title: "",
      content: "",
    };
    setFormData((prev) => ({
      ...prev,
      liveNotes: [...prev.liveNotes, newNote],
    }));
  };

  // Update live note
  const handleUpdateLiveNote = (noteId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      liveNotes: prev.liveNotes.map((note) =>
        note.id === noteId ? { ...note, [field]: value } : note
      ),
    }));
  };

  // Remove live note
  const handleRemoveLiveNote = (noteId) => {
    setFormData((prev) => ({
      ...prev,
      liveNotes: prev.liveNotes.filter((note) => note.id !== noteId),
    }));
  };

  // Save handwriting strokes
  const handleSaveHandwriting = (newStrokes, newCanvasSize) => {
    setFormData((prev) => ({
      ...prev,
      handwritingStrokes: newStrokes,
      handwritingCanvasSize: newCanvasSize,
    }));
    setShowHandwritingCanvas(false);
  };

  // Manual save
  const handleManualSave = async () => {
    await performAutoSave();
    showSuccess("נשמר בהצלחה");
  };

  // End session
  const handleEndSession = async () => {
    if (!activeSessionId) return;

    // Show global loader immediately
    dispatch(showGlobalLoader("מסיים את הפגישה..."));
    setShowEndConfirm(false);

    try {
      await endSession({
        clinicId,
        sessionId: activeSessionId,
        sessionData: {
          ...formData,
          patientId,
          patientName: patient ? `${patient.firstName} ${patient.lastName}` : "",
          appointmentId,
        },
        therapistId,
        therapistName,
      }).unwrap();

      // Invalidate calendar appointments cache to update the OngoingAppointmentIndicator
      // This ensures the appointment status change (to "completed") is reflected immediately
      dispatch(
        calendarApi.util.invalidateTags([
          { type: "Appointments", id: "LIST" },
          { type: "Appointments", id: appointmentId },
        ])
      );

      // Small delay to allow cache invalidation and refetch to complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      showSuccess("סשן לייב הסתיים ונשמר");
      dispatch(hideGlobalLoader());
      navigate("/dashboard");
    } catch (error) {
      console.error("Error ending session:", error);
      dispatch(hideGlobalLoader());
      showError("שגיאה בסיום הסשן");
    }
  };

  // AI Tools handlers
  const handleOpenAiTool = (toolType) => {
    setAiToolInput("");
    setAiToolModal(toolType);
  };

  const handleCloseAiTool = () => {
    setAiToolModal(null);
    setAiToolInput("");
  };

  const handleSubmitAiTool = async () => {
    if (!aiToolInput.trim() || aiLoading) return;

    const question = aiToolInput.trim();
    const toolType = aiToolModal;

    // Close modal immediately and show full-screen loading
    setAiToolModal(null);
    setAiToolInput("");
    setAiLoading(true);

    try {
      let result;

      if (toolType === "game") {
        trackFeature(FEATURE_KEYS.GAMES);
        result = await aiGameSelection({
          clinicId,
          patientId,
          therapistGoal: question,
        });
      } else if (toolType === "patient") {
        result = await aiPatientConsultation({
          clinicId,
          patientId,
          therapistQuestion: question,
        });
      } else if (toolType === "treatment") {
        // First, perform a silent auto-save
        await performAutoSave();
        
        result = await aiTreatmentConsultation({
          clinicId,
          patientId,
          currentFormData: formData,
          therapistQuestion: question,
        });
      }

      if (result?.success) {
        // Add the response to the responses array
        const newResponse = {
          id: `ai_${Date.now()}`,
          type: toolType,
          question,
          response: result.response,
          timestamp: new Date().toISOString(),
        };
        setAiResponses((prev) => [newResponse, ...prev]);
      } else {
        console.error("AI Tool error:", result?.error);
        showError(result?.error || "שגיאה בקבלת תשובה מהAI");
      }
    } catch (error) {
      console.error("AI Tool exception:", {
        toolType,
        error: error.message,
        stack: error.stack,
      });
      showError("שגיאה בקבלת תשובה מהAI. נסה שוב.");
    } finally {
      setAiLoading(false);
    }
  };

  // Get tool title by type
  const getAiToolTitle = (type) => {
    switch (type) {
      case "game":
        return "בחירת משחק ופעילות";
      case "patient":
        return "התייעצות בלייב לגבי המטופל";
      case "treatment":
        return "התייעצות לגבי הטיפול עד כה";
      default:
        return "";
    }
  };

  // Get tool icon by type
  const getAiToolIcon = (type) => {
    switch (type) {
      case "game":
        return <GameController size={18} weight="duotone" />;
      case "patient":
        return <User size={18} weight="duotone" />;
      case "treatment":
        return <Brain size={18} weight="duotone" />;
      default:
        return <Sparkle size={18} weight="duotone" />;
    }
  };

  // Get modal placeholder by type
  const getAiToolPlaceholder = (type) => {
    switch (type) {
      case "game":
        return "לאיזו מטרה אתה צריך משחק? (לדוגמה: משחק לתרגול קשב וריכוז)";
      case "patient":
        return "צא/י מנקודת הנחה שאני מכיר/ה את המטופל/ת - מה השאלה שלך?";
      case "treatment":
        return "אני כרגע בטיפול הזה - מה השאלה שלך?";
      default:
        return "";
    }
  };

  // Toggle fullscreen (using browser Fullscreen API)
  const pageRef = useRef(null);
  
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (pageRef.current?.requestFullscreen) {
          await pageRef.current.requestFullscreen();
        } else if (pageRef.current?.webkitRequestFullscreen) {
          await pageRef.current.webkitRequestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
      // Fallback to CSS-only fullscreen
      setIsFullscreen(!isFullscreen);
    }
  };

  // Listen for fullscreen changes (e.g., ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Current template
  const currentTemplate =
    formData.templateId === "default"
      ? DEFAULT_TEMPLATE
      : templates.find((t) => t.id === formData.templateId) || DEFAULT_TEMPLATE;

  // Loading state
  if (isLoadingPatient || isLoadingSession) {
    return (
      <div className={`live-session-page ${isFullscreen ? "fullscreen" : ""}`}>
        <ContentLoader fill message="טוען סשן לייב..." />
      </div>
    );
  }

  // No patient
  if (!patient) {
    return (
      <div className={`live-session-page ${isFullscreen ? "fullscreen" : ""}`}>
        <div className="live-session-error">
          <WarningCircle size={48} weight="duotone" />
          <span>מטופל לא נמצא</span>
          <button onClick={() => navigate("/dashboard/calendar")}>
            חזרה ללוח שנה
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={pageRef}
      className={`live-session-page ${isFullscreen ? "fullscreen" : ""}`}
    >
      {/* Watermark Logo (only in fullscreen) */}
      {isFullscreen && (
        <div className="live-session-watermark">
          <img src="/logo512.png" alt="" />
        </div>
      )}

      {/* Header */}
      <div className="live-session-header">
        <div className="header-right">
          {/* Timer Section */}
          <div className="session-timer-section">
            <div className="timer-item">
              <span className="timer-label">שעון:</span>
              <div className="session-clock">
                <Clock size={18} weight="regular" />
                <span>{formatTime(currentTime)}</span>
              </div>
            </div>
            <div className="timer-divider" />
            <div className="timer-item">
              <span className="timer-label">זמן נותר לפגישה:</span>
              <div className="session-remaining">
                <Timer size={18} weight="regular" />
                <span>{remainingTime}</span>
              </div>
            </div>
          </div>

          {/* End Session Button */}
          <button
            className="end-session-btn"
            onClick={() => setShowEndConfirm(true)}
            disabled={isEnding}
          >
            <Stop size={16} weight="regular" />
            <span>סיום פגישה</span>
          </button>
        </div>

        <div className="header-left">
          <HelpButton featureKey="live_session" />
          {/* Auto-save status */}
          {autoSaveStatus && (
            <span className={`auto-save-indicator ${autoSaveStatus}`}>
              {autoSaveStatus === "saving" ? (
                <>
                  <LoaderLoop size="xs" />
                  שומר אוטומטית...
                </>
              ) : (
                <>
                  <Check size={14} weight="bold" />
                  נשמר
                </>
              )}
            </span>
          )}

          {/* Manual Save */}
          <button
            className="save-btn"
            onClick={handleManualSave}
            disabled={isUpdating || autoSaveStatus === "saving"}
          >
            <FloppyDisk size={18} weight="bold" />
            <span>שמירה</span>
          </button>

          {/* Fullscreen Toggle */}
          <button className="fullscreen-btn" onClick={toggleFullscreen}>
            {isFullscreen ? (
              <ArrowsIn size={20} weight="bold" />
            ) : (
              <ArrowsOut size={20} weight="bold" />
            )}
          </button>
        </div>
      </div>

      {/* Patient Info Bar */}
      <div className="patient-info-bar">
        <div className="patient-avatar">
          {patient.firstName?.charAt(0)}
          {patient.lastName?.charAt(0)}
        </div>
        <div className="patient-info-details">
          <span className="patient-name">
            {patient.firstName} {patient.lastName}
          </span>
          <span className="session-info">
            <CalendarBlank size={14} />
            {new Date().toLocaleDateString("he-IL", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
            {" • "}
            <Clock size={14} />
            {ongoingAppointment
              ? formatTime(new Date(ongoingAppointment.startTime))
              : formatTime(new Date())}
          </span>
        </div>
        <div className="session-status-badge">
          <Play size={12} weight="fill" />
          סשן לייב פעיל
        </div>
      </div>

      {/* Main Content */}
      <div className="live-session-content">
        <div className="live-session-scroll">
          {/* Template Selection */}
          <div className="live-session-section">
            <div className="section-header">
              <h3>
                <FileText size={20} weight="duotone" />
                בחירת תבנית
              </h3>
            </div>
            <div className="template-selection">
              <button
                type="button"
                className={`template-option ${
                  formData.templateId === "default" ? "selected" : ""
                }`}
                onClick={() => handleTemplateSelect(DEFAULT_TEMPLATE)}
              >
                <FileText size={16} />
                סיכום בלייב
              </button>

              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className={`template-option ${
                    formData.templateId === template.id ? "selected" : ""
                  }`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <FileText size={16} />
                  {template.name}
                </button>
              ))}
            </div>
          </div>

          {/* AI Tools Section */}
          <div className="live-session-section tools-section">
            <div className="section-header">
              <h3>
                <Sparkle size={20} weight="duotone" />
                כלים ועזרים
              </h3>
            </div>

            {/* Last Session Summary */}
            <div className="tool-summary-box">
              <div className="summary-label">
                <NotePencil size={16} weight="duotone" />
                סיכום פגישה אחרונה
              </div>
              <div className="summary-content">{lastTreatmentSummary}</div>
            </div>

            {/* AI Patient Summary */}
            {clinicProfile?.enableAiPatientSummary === true && patient?.patientSummary && (
              <div className="tool-summary-box ai-summary-box">
                <div className="summary-label">
                  <Sparkle size={16} weight="duotone" />
                  סיכום מטופל AI
                </div>
                <div className="summary-content">{patient.patientSummary}</div>
              </div>
            )}

            {/* AI Tool Buttons */}
            <div className="ai-tools-grid">
              {isSpeechTherapist && (
              <button className="ai-tool-btn" onClick={() => handleOpenAiTool("game")}>
                <div className="ai-tool-icon">
                  <GameController size={24} weight="duotone" />
                </div>
                <div className="ai-tool-info">
                  <span className="ai-tool-title">בחירת משחק ופעילות</span>
                  <span className="ai-tool-desc">
                    קבל המלצות למשחקים ופעילויות מותאמות למטרות הטיפול ולגיל המטופל
                  </span>
                </div>
              </button>
              )}

              <button className="ai-tool-btn" onClick={() => handleOpenAiTool("patient")}>
                <div className="ai-tool-icon">
                  <User size={24} weight="duotone" />
                </div>
                <div className="ai-tool-info">
                  <span className="ai-tool-title">התייעצות בלייב לגבי המטופל</span>
                  <span className="ai-tool-desc">
                    שאל שאלות והתייעץ עם AI לגבי המטופל הספציפי, היסטוריה ומאפיינים
                  </span>
                </div>
              </button>

              <button className="ai-tool-btn" onClick={() => handleOpenAiTool("treatment")}>
                <div className="ai-tool-icon">
                  <Brain size={24} weight="duotone" />
                </div>
                <div className="ai-tool-info">
                  <span className="ai-tool-title">התייעצות בלייב לגבי הטיפול עד כה</span>
                  <span className="ai-tool-desc">
                    קבל תובנות והמלצות לגבי מהלך הטיפול, התקדמות ושלבים הבאים
                  </span>
                </div>
              </button>
            </div>

            {/* AI Responses Display */}
            {aiResponses.length > 0 && (
              <div className="ai-responses-container">
                {aiResponses.map((response) => (
                  <div key={response.id} className="ai-response-box">
                    <div className="ai-response-header">
                      <div className="ai-response-type">
                        {getAiToolIcon(response.type)}
                        <span>{getAiToolTitle(response.type)}</span>
                      </div>
                      <button
                        className="ai-response-close"
                        onClick={() =>
                          setAiResponses((prev) =>
                            prev.filter((r) => r.id !== response.id)
                          )
                        }
                      >
                        <X size={16} weight="bold" />
                      </button>
                    </div>
                    <div className="ai-response-question">
                      <span className="question-label">השאלה שלך:</span>
                      <span className="question-text">{response.question}</span>
                    </div>
                    <div className="ai-response-content">
                      {response.response.split("\n").map((line, index) => {
                        // Helper function to parse bold text (**text**)
                        const parseBoldText = (text) => {
                          const parts = text.split(/(\*\*[^*]+\*\*)/g);
                          return parts.map((part, i) => {
                            if (part.startsWith("**") && part.endsWith("**")) {
                              return (
                                <strong key={i} className="ai-bold">
                                  {part.slice(2, -2)}
                                </strong>
                              );
                            }
                            return part;
                          });
                        };

                        // Handle ### headers
                        if (line.startsWith("### ")) {
                          return (
                            <h5 key={index} className="ai-response-subheading">
                              {parseBoldText(line.replace("### ", ""))}
                            </h5>
                          );
                        }
                        // Handle ## headers
                        if (line.startsWith("## ")) {
                          return (
                            <h4 key={index} className="ai-response-heading">
                              {parseBoldText(line.replace("## ", ""))}
                            </h4>
                          );
                        }
                        // Handle bullet points
                        if (line.startsWith("- ") || line.startsWith("• ")) {
                          return (
                            <p key={index} className="ai-response-bullet">
                              <span className="bullet-icon">•</span>
                              <span>{parseBoldText(line.replace(/^[-•]\s/, ""))}</span>
                            </p>
                          );
                        }
                        // Handle numbered lists
                        if (/^\d+\.\s/.test(line)) {
                          const match = line.match(/^(\d+)\.\s(.*)$/);
                          return (
                            <p key={index} className="ai-response-numbered">
                              <span className="number-icon">{match[1]}.</span>
                              <span>{parseBoldText(match[2])}</span>
                            </p>
                          );
                        }
                        // Handle empty lines
                        if (line.trim() === "") {
                          return <div key={index} className="ai-response-spacer" />;
                        }
                        // Regular text
                        return (
                          <p key={index} className="ai-response-paragraph">
                            {parseBoldText(line)}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Template Fields Section */}
          <div className="live-session-section">
            <div className="section-header">
              <h3>
                <Stethoscope size={20} weight="duotone" />
                {currentTemplate.name}
              </h3>
            </div>

            <div className="template-fields">
              {formData.fields.map((field) => (
                <div key={field.id} className="template-field">
                  <label className={field.required ? "required" : ""}>
                    {field.label}
                  </label>
                  {field.type === "long_text" ? (
                    <RichTextEditor
                      value={field.value || ""}
                      onChange={(value) => handleFieldChange(field.id, value)}
                      placeholder={field.placeholder || `הכנס ${field.label}...`}
                      size="long"
                    />
                  ) : (
                    <input
                      type="text"
                      value={field.value || ""}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      placeholder={field.placeholder || `הכנס ${field.label}...`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Game Selection - Speech Therapist Only */}
          {isSpeechTherapist && clinicGames.length > 0 && (
            <div className="live-session-section games-section">
              <div className="section-header">
                <h3>
                  <GameController size={20} weight="duotone" />
                  בחירת משחק
                </h3>
              </div>
              <div className="games-grid">
                {clinicGames.map((game) => {
                  const isSelected = formData.selectedGameIds.includes(game.id);
                  const gameGoals = getGoalsByIds(game.goalIds || []);

                  return (
                    <button
                      key={game.id}
                      type="button"
                      className={`game-card ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          selectedGameIds: isSelected
                            ? prev.selectedGameIds.filter((id) => id !== game.id)
                            : [...prev.selectedGameIds, game.id],
                        }));
                      }}
                    >
                      <div className="game-card-header">
                        <span className="game-card-name">{game.name}</span>
                        {isSelected && (
                          <span className="game-selected-check">
                            <Check size={14} weight="bold" />
                          </span>
                        )}
                      </div>
                      {gameGoals.length > 0 && (
                        <div className="game-card-goals">
                          {gameGoals.slice(0, 2).map((goal) => (
                            <span
                              key={goal.id}
                              className="game-goal-tag"
                              style={{
                                backgroundColor: goal.categoryColor + "15",
                                color: goal.categoryColor,
                              }}
                            >
                              {goal.name}
                            </span>
                          ))}
                          {gameGoals.length > 2 && (
                            <span className="game-goals-more">
                              +{gameGoals.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Live Notes Section */}
          <div className="live-session-section notes-section">
            <div className="section-header">
              <h3>
                <Lightning size={20} weight="duotone" />
                הערות בלייב
              </h3>
            </div>

            {/* Existing Notes */}
            <div className="live-notes-list">
              {formData.liveNotes.map((note, index) => (
                <div key={note.id} className="live-note-item">
                  <div className="live-note-header">
                    <span className="live-note-number">הערה {index + 1}</span>
                    <button
                      className="live-note-delete"
                      onClick={() => handleRemoveLiveNote(note.id)}
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                  <input
                    type="text"
                    className="live-note-title"
                    placeholder="כותרת..."
                    value={note.title || ""}
                    onChange={(e) =>
                      handleUpdateLiveNote(note.id, "title", e.target.value)
                    }
                  />
                  <textarea
                    className="live-note-content"
                    placeholder="תוכן ההערה..."
                    value={note.content || ""}
                    onChange={(e) =>
                      handleUpdateLiveNote(note.id, "content", e.target.value)
                    }
                    rows={3}
                  />
                </div>
              ))}
            </div>

            {/* Handwriting Preview */}
            <HandwritingPreview
              strokes={formData.handwritingStrokes}
              canvasSize={formData.handwritingCanvasSize}
              onClick={() => setShowHandwritingCanvas(true)}
            />

            {/* Add Note Button */}
            <button className="add-live-note-btn" onClick={handleAddLiveNote}>
              <Plus size={20} weight="bold" />
              <span>הוסף הערה חדשה</span>
            </button>

            {/* iPad Handwriting Button - shown only on tablet touch devices */}
            <button
              className="ipad-handwriting-btn"
              onClick={() => setShowHandwritingCanvas(true)}
            >
              <PencilSimple size={20} weight="duotone" />
              <span>כתיבת הערות בכתב יד</span>
            </button>
          </div>
        </div>
      </div>

      {/* End Session Confirmation Modal */}
      {showEndConfirm && (
        <div className={`end-session-modal ${isOnboardingActive ? 'onboarding-active' : ''}`}>
          <div className="end-session-content">
            <div className="end-session-icon">
              <Stop size={32} weight="fill" />
            </div>
            <h3>סיום סשן לייב</h3>
            <p>
              האם אתה בטוח שברצונך לסיים את הסשן?
              <br />
              הנתונים יישמרו ותוכל לייבא אותם לסיכום הפגישה.
            </p>
            <div className="end-session-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowEndConfirm(false)}
                disabled={isOnboardingActive}
              >
                ביטול
              </button>
              <button
                className="confirm-btn"
                onClick={handleEndSession}
                disabled={isEnding}
              >
                {isEnding ? (
                  <LoaderLoop size="xs" />
                ) : (
                  <Stop size={16} weight="fill" />
                )}
                סיים סשן
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Tool Modal */}
      {aiToolModal && (
        <div className="ai-tool-modal-overlay">
          <div className="ai-tool-modal">
            <div className="ai-tool-modal-header">
              <h3>
                {getAiToolIcon(aiToolModal)}
                {getAiToolTitle(aiToolModal)}
              </h3>
              <ModalCloseButton onClick={handleCloseAiTool} />
            </div>
            <div className="ai-tool-modal-body">
              <textarea
                className="ai-tool-textarea"
                placeholder={getAiToolPlaceholder(aiToolModal)}
                value={aiToolInput}
                onChange={(e) => setAiToolInput(e.target.value)}
                rows={4}
                autoFocus
              />
            </div>
            <div className="ai-tool-modal-footer">
              <button className="ai-tool-cancel-btn" onClick={handleCloseAiTool}>
                ביטול
              </button>
              <button
                className="ai-tool-submit-btn"
                onClick={handleSubmitAiTool}
                disabled={!aiToolInput.trim()}
              >
                <Sparkle size={18} weight="fill" />
                שלח שאלה
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Full Screen Loading */}
      {aiLoading && (
        <div className="ai-loading-overlay">
          <div className="ai-loading-content">
            <div className="ai-loading-spinner">
              <LoaderLoop size="lg" />
            </div>
            <div className="ai-loading-text">
              <Sparkle size={24} weight="fill" />
              <span>מקבל תשובה מהעוזר החכם...</span>
            </div>
          </div>
        </div>
      )}

      {/* Handwriting Canvas Modal */}
      {showHandwritingCanvas && (
        <HandwritingCanvas
          strokes={formData.handwritingStrokes}
          onSave={handleSaveHandwriting}
          onClose={() => setShowHandwritingCanvas(false)}
        />
      )}
    </div>
  );
}

export default LiveSessionPage;
